/**
 * Minouko — comportements de la boutique.
 * JavaScript natif, aucun framework, aucune dépendance.
 * Tout ce qui est ici dégrade proprement : sans JS, les formulaires postent
 * normalement et les liens fonctionnent.
 */
(() => {
  'use strict';

  const euro = (cents) =>
    (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
    ' €';

  /** Le CDN Shopify redimensionne via ?width=, pas via le suffixe _200x hérité. */
  const sized = (url, w) => {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (_) {
      return url;
    }
  };

  /**
   * Les API Shopify n'expriment pas les prix de la même façon : l'API panier et
   * celle des recommandations renvoient des centiemes entiers, celle de
   * suggestion une chaîne décimale. Sans normalisation, un prix sort cent fois
   * trop élevé.
   */
  const toCents = (v) => {
    if (typeof v === 'number') return Math.round(v);
    const str = String(v).trim();
    if (/[.,]/.test(str)) return Math.round(parseFloat(str.replace(',', '.')) * 100);
    return Math.round(Number(str));
  };

  const focusable =
    'a[href], summary, button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ───────────────────────── Tiroir panier ───────────────────────── */

  const Drawer = {
    el: null,
    panel: null,
    body: null,
    foot: null,
    lastFocus: null,

    init() {
      this.el = document.getElementById('cart-drawer');
      if (!this.el) return;
      this.panel = this.el.querySelector('.drawer__panel');
      this.body = this.el.querySelector('[data-drawer-body]');
      this.foot = this.el.querySelector('[data-drawer-foot]');

      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.el.querySelector('.drawer__veil')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
        if (e.key === 'Tab' && this.isOpen()) this.trap(e);
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cart-open]')) return;
        e.preventDefault();
        this.open();
      });

      this.body.addEventListener('click', (e) => {
        const step = e.target.closest('[data-step]');
        if (step) {
          e.preventDefault();
          const line = Number(step.dataset.line);
          const qty = Number(step.dataset.qty);
          this.change(line, qty, step);
        }
      });
    },

    isOpen() {
      return this.el && this.el.hasAttribute('open');
    },

    trap(e) {
      const nodes = [...this.panel.querySelectorAll(focusable)].filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    async open(source) {
      if (!this.el) return;
      this.lastFocus = source || document.activeElement;
      this.el.inert = false;
      this.el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      const target = this.panel.querySelector('button[data-drawer-close]');
      if (target) target.focus();
      await this.refresh();
    },

    close() {
      if (!this.el) return;
      this.el.removeAttribute('open');
      this.el.inert = true;
      document.documentElement.style.overflow = '';
      if (this.lastFocus) this.lastFocus.focus();
    },

    async refresh() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        this.render(cart);
        Drawer.setCount(cart.item_count);
      } catch (_) {
        this.body.innerHTML = '<p class="drawer__msg">Le panier est momentanément indisponible. Rechargez la page.</p>';
      }
    },

    setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = n > 0 ? n : '';
        el.hidden = n === 0;
      });
    },

    render(cart) {
      if (!cart.items.length) {
        this.body.innerHTML =
          '<div class="drawer__empty"><p>Votre panier est vide.</p>' +
          '<p><a href="/collections">Parcourir les rayons</a></p></div>';
        this.foot.hidden = true;
        return;
      }
      this.foot.hidden = false;
      this.body.innerHTML =
        '<ul class="dlines">' +
        cart.items
          .map((it, i) => {
            const line = i + 1;
            const img = it.image ? `<img src="${sized(it.image, 200)}" alt="" decoding="async">` : '';
            const variant =
              it.options_with_values && it.options_with_values.some(o => o.value !== 'Default Title')
                ? `<p class="dline__v">${it.options_with_values
                    .filter((o) => !/ships?\s*from|origin/i.test(o.name))
                    .map((o) => o.value)
                    .join(' · ')}</p>`
                : '';
            return `<li class="dline" data-item-key="${escapeProperty(it.key || String(line))}">
              <a class="dline__ph" href="${it.url}" tabindex="-1" aria-hidden="true">${img}</a>
              <div class="dline__body">
                <h3 class="dline__t"><a href="${it.url}">${it.product_title}</a></h3>
                ${variant}
                <dl class="line-properties">${Object.entries(it.properties || {})
                  .filter(([name, value]) => !name.startsWith('_') && value != null && value !== '')
                  .map(([name, value]) => `<div><dt>${escapeProperty(name)}</dt><dd>${escapeProperty(value)}</dd></div>`)
                  .join('')}</dl>
                <div class="stepper" role="group" aria-label="Quantité">
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity - 1}" aria-label="Retirer un exemplaire">&minus;</button>
                  <span class="num" aria-live="polite">${it.quantity}</span>
                  <button type="button" data-step data-line="${line}" data-qty="${it.quantity + 1}" aria-label="Ajouter un exemplaire">+</button>
                </div>
              </div>
              <div class="dline__end">
                <span class="num">${euro(it.final_line_price)}</span>
                <button type="button" class="dline__rm" data-step data-line="${line}" data-qty="0">Retirer</button>
              </div>
            </li>`;
          })
          .join('') +
        '</ul>';
      this.foot.querySelector('[data-drawer-total]').textContent = euro(cart.total_price);
      this.crossSell(cart);
    },

    /**
     * Vente croisée. On part du dernier article ajouté et on propose ses
     * voisins de rayon, en excluant ce qui est déjà au panier. Rien ne
     * s'affiche si Shopify ne renvoie rien : pas de bloc vide.
     */
    async crossSell(cart) {
      const host = this.el.querySelector('[data-drawer-cross]');
      if (!host || !cart.items.length) return;
      const seed = cart.items[cart.items.length - 1];
      if (host.dataset.seed === String(seed.product_id)) return;
      host.dataset.seed = String(seed.product_id);
      const inCart = new Set(cart.items.map((i) => i.product_id));
      try {
        const d = await fetch(
          `/recommendations/products.json?product_id=${seed.product_id}&limit=6&intent=related`,
          { headers: { Accept: 'application/json' } }
        ).then((r) => r.json());
        const picks = (d.products || []).filter((p) => !inCart.has(p.id)).slice(0, 3);
        if (!picks.length) {
          host.innerHTML = '';
          return;
        }
        host.innerHTML =
          '<h3 class="cross__h">À compléter</h3><ul class="cross">' +
          picks
            .map((p) => {
              const src =
                (p.featured_image && (p.featured_image.url || p.featured_image)) ||
                (p.images && p.images[0]) ||
                null;
              const img = src ? `<img src="${sized(src, 120)}" alt="" decoding="async">` : '';
              return `<li><a href="${p.url}">
                <span class="cross__ph">${img}</span>
                <span class="cross__t">${p.title}</span>
                <span class="price num">${euro(toCents(p.price))}</span>
              </a></li>`;
            })
            .join('') +
          '</ul>';
      } catch (_) {
        host.innerHTML = '';
      }
    },

    async change(line, quantity, trigger) {
      if (this.body.getAttribute('aria-busy') === 'true') return;
      const itemKey = trigger?.closest('[data-item-key]')?.dataset.itemKey;
      const action = trigger?.getAttribute('aria-label') || trigger?.textContent;
      const buttons = [...this.body.querySelectorAll('[data-step]')];
      const disabled = buttons.map(button => button.disabled);
      this.body.querySelector('[data-drawer-error]')?.remove();
      this.body.setAttribute('aria-busy', 'true');
      buttons.forEach(button => { button.disabled = true; });
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
          signal: controller.signal,
        });
        const cart = await response.json();
        if (!response.ok || !Array.isArray(cart.items)) {
          const error = new Error('cart-change');
          error.stock = response.status === 422;
          throw error;
        }
        this.render(cart);
        Drawer.setCount(cart.item_count);
        const row = itemKey && [...this.body.querySelectorAll('[data-item-key]')].find(el => el.dataset.itemKey === itemKey);
        const next = row && [...row.querySelectorAll('[data-step]')].find(el => (el.getAttribute('aria-label') || el.textContent) === action);
        (next || this.body.querySelector('[data-step]') || this.panel.querySelector('[data-drawer-close]'))?.focus({ preventScroll: true });
      } catch (error) {
        const feedback = document.createElement('p');
        feedback.className = 'drawer__error';
        feedback.dataset.drawerError = '';
        feedback.setAttribute('role', 'alert');
        feedback.textContent = error.stock
          ? 'Cette quantité n’est pas disponible. Choisissez une quantité plus petite ou réessayez.'
          : 'La mise à jour n’a pas pu être confirmée. Réessayez ou rouvrez le panier pour vérifier son contenu.';
        this.body.append(feedback);
      } finally {
        clearTimeout(timeout);
        this.body.removeAttribute('aria-busy');
        buttons.forEach((button, index) => { if (button.isConnected) button.disabled = disabled[index]; });
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      }
    },
  };

  /* ─────────────── Ajout au panier, depuis n'importe où ─────────────── */

  function escapeProperty(value) {
    const text = document.createElement('span');
    text.textContent = String(value);
    return text.innerHTML;
  }

  function submitProduct(form, button) {
    if (form.dataset.submitting) return;
    const idField = form.querySelector('[name="id"]');
    if (!idField || idField.disabled || !idField.value) return;
    form.querySelectorAll('[data-engraving-field]').forEach((field) => {
      field.value = field.value.trim();
    });
    if (!form.reportValidity()) return;
    const properties = {};
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    });
    const qty = form.querySelector('[name="quantity"]');
    form.dataset.submitting = 'true';
    addToCart(idField.value, qty ? Number(qty.value) : 1, button, properties)
      .finally(() => { delete form.dataset.submitting; });
  }

  async function addToCart(id, quantity, button, properties = {}) {
    if (button?.disabled) return;
    const cardLabel = button?.querySelector('[data-card-add-label]');
    const cardLabelText = cardLabel?.textContent;
    if (cardLabel) cardLabel.textContent = 'Ajout…';
    let feedback = null;
    if (button) {
      const host = button.closest('[data-product-form], [data-card-product-form], .cartel, .stickybuy__in') || button.parentElement;
      feedback = host.querySelector('[data-cart-feedback]');
      if (!feedback) {
        feedback = document.createElement('p');
        feedback.setAttribute('data-cart-feedback', '');
        feedback.setAttribute('role', 'status');
        host.append(feedback);
      }
      feedback.textContent = '';
    }
    if (button) {
      button.disabled = true;
      button.dataset.busy = 'true';
      button.setAttribute('aria-busy', 'true');
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1, properties }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Ajout impossible.');
      }
      await Drawer.open(button);
    } catch (e) {
      if (feedback) feedback.textContent = e.message || 'Ajout impossible. Réessayez dans un instant.';
    } finally {
      if (button) {
        button.disabled = false;
        delete button.dataset.busy;
        button.removeAttribute('aria-busy');
        if (cardLabel) cardLabel.textContent = cardLabelText;
      }
    }
  }

  // Cancel native product submission before Shopify's form-level analytics listener.
  // The successful cart/add.js response remains the single add-to-cart signal.
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form], [data-card-product-form]');
    if (!form) return;
    e.preventDefault();
    submitProduct(form, form.querySelector('[type="submit"]'));
  }, { capture: true });

  /* ───────────────────────── Galerie produit ───────────────────────── */

  function gallery() {
    const root = document.querySelector('[data-gallery]');
    if (!root || root.dataset.galleryReady) return;
    const original = root.querySelector('[data-gallery-main]');
    if (!original) return;
    root.dataset.galleryReady = 'true';
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const zoom = root.querySelector('[data-gallery-zoom]');
    const dialog = document.querySelector('[data-gallery-dialog]');
    const caption = root.querySelector('[data-gallery-caption]');
    let count = root.querySelector('[data-gallery-count]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const initial = thumbs.findIndex(t => t.getAttribute('aria-current') === 'true');
    const entries = thumbs.map(t => ({ ...t.dataset }));
    // A variant image can legitimately be absent from the editorial thumbnail list.
    const originalEntry = { imageId: root.dataset.galleryInitialId, full: original.currentSrc || original.src, master: original.dataset.galleryMaster || original.src, alt: original.alt, frame: original.parentElement.style.cssText, caption: caption?.textContent || '' };
    if (initial < 0) entries.push(originalEntry);
    let selected = initial < 0 ? entries.length - 1 : initial;
    let main = original;
    let imageRequest = 0;
    let dialogTrack;
    let dialogImages = [];
    const stage = original.parentElement;
    const track = document.createElement('div');
    track.className = 'pdp__track';
    track.setAttribute('role', 'region');
    track.setAttribute('aria-roledescription', 'carrousel');
    track.setAttribute('aria-label', 'Photos du produit');
    track.tabIndex = 0;
    stage.before(track);
    const makeSlide = (entry, index, reuse = false) => {
      const slide = document.createElement('div');
      slide.className = 'pdp__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'diapositive');
      const well = reuse ? stage : document.createElement('div');
      well.className = 'pdp__stage' + (entry.frame ? ' pdp__stage--framed' : '');
      well.style.cssText = entry.frame || '';
      const img = reuse ? original : document.createElement('img');
      if (!reuse) {
        img.className = 'pdp__img';
        img.alt = entry.alt || '';
        img.width = original.width; img.height = original.height;
        img.loading = 'lazy'; img.decoding = 'async';
        img.sizes = '(min-width: 940px) 52vw, 100vw';
        // Une image sans src ne déclenche aucune requête. La galerie conserve
        // ses diapositives pour que le défilement reste natif, mais ne charge
        // que la vue affichée et ses voisines immédiates.
        img.dataset.srcset = [360, 640, 900, 1200, 1600].map(width => `${sized(entry.full, width)} ${width}w`).join(', ');
        img.dataset.src = sized(entry.full, 900);
        well.append(img);
      }
      img.draggable = false;
      slide.append(well); track.append(slide);
      return slide;
    };
    const slides = entries.map((entry, index) => makeSlide(entry, index, index === selected));
    const loadSlide = (index, priority = 'eager') => {
      const img = slides[index]?.querySelector('img');
      if (!img || !img.dataset.src) return;
      img.loading = priority;
      if (priority === 'high') img.fetchPriority = 'high';
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
    };
    const warm = index => {
      [index - 1, index, index + 1].forEach(i => {
        const img = slides[i]?.querySelector('img');
        if (img) {
          loadSlide(i);
          img.loading = 'eager';
        }
      });
    };
    const dialogStatus = dialog?.querySelector('[data-gallery-load-status]');
    const updateDialog = () => {
      if (!dialog?.open) return;
      const entry = entries[selected];
      const enlarged = dialogImages[selected];
      const request = ++imageRequest;
      dialog.querySelector('[data-gallery-dialog-count]').textContent = `${selected + 1} / ${entries.length}`;
      dialog.querySelector('[data-gallery-dialog-caption]').textContent = entry.caption || '';
      const previous = dialog.querySelector('[data-gallery-prev]');
      const next = dialog.querySelector('[data-gallery-next]');
      previous.hidden = next.hidden = entries.length < 2;
      previous.disabled = selected === 0;
      next.disabled = selected === entries.length - 1;
      if ((document.activeElement === previous && previous.disabled) || (document.activeElement === next && next.disabled)) dialogTrack.focus({ preventScroll: true });
      dialogImages.forEach((img, i) => {
        if (Math.abs(i - selected) <= 1) {
          img.loading = 'eager';
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
        }
        img.parentElement.setAttribute('aria-label', `${i + 1} sur ${entries.length}`);
      });
      dialogStatus.textContent = 'Chargement de l’image…';
      dialogStatus.hidden = false;
      const full = new Image();
      full.onload = () => {
        if (request !== imageRequest || !dialog.open) return;
        enlarged.src = full.src;
        dialogStatus.hidden = true;
      };
      full.onerror = () => {
        if (request !== imageRequest || !dialog.open) return;
        dialogStatus.textContent = 'Grand format indisponible. La vue actuelle reste affichée.';
      };
      full.src = entry.master || entry.full;
    };
    const activate = index => {
      selected = index;
      const entry = entries[index];
      main.removeAttribute('data-gallery-main'); main.removeAttribute('id');
      main = slides[index].querySelector('img');
      main.setAttribute('data-gallery-main', ''); main.id = 'gallery-main';
      main.dataset.galleryMaster = entry.master || entry.full;
      thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      slides.forEach((slide, i) => slide.setAttribute('aria-label', `${i + 1} sur ${entries.length}`));
      if (caption) { caption.textContent = entry.caption || ''; caption.hidden = !caption.textContent; }
      if (!count && entries.length > 1) {
        count = document.createElement('span');
        count.className = 'pdp__gallery-count';
        count.setAttribute('data-gallery-count', '');
        root.querySelector('.pdp__gallery-note')?.append(count);
      }
      if (count) {
        count.textContent = `${index + 1} / ${entries.length}`;
        count.setAttribute('aria-label', `Visuel ${index + 1} sur ${entries.length}`);
      }
      const thumb = thumbs[index], strip = thumb?.parentElement;
      if (strip) {
        const offset = thumb.offsetLeft - strip.offsetLeft;
        if (offset < strip.scrollLeft) strip.scrollLeft = offset;
        else if (offset + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = offset + thumb.offsetWidth - strip.clientWidth;
      }
      warm(index); updateDialog();
    };
    // The browser owns the gesture and inertia. Only synchronize metadata here.
    const controllers = new Map();
    const connect = (element, fromDialog = false) => {
      let frame = 0, timer;
      const state = { pending: null };
      controllers.set(element, state);
      const read = () => {
        frame = 0;
        if (state.pending !== null || !element.clientWidth) return;
        const index = Math.max(0, Math.min(entries.length - 1, Math.round(element.scrollLeft / element.clientWidth)));
        if (index === selected) return;
        activate(index);
        if (fromDialog) align(track, index, false);
      };
      const settle = () => { state.pending = null; read(); };
      element.addEventListener('scroll', () => {
        if (!frame) frame = requestAnimationFrame(read);
        clearTimeout(timer); timer = setTimeout(settle, 140);
      }, { passive: true });
      element.addEventListener('scrollend', settle);
      element.addEventListener('pointerdown', () => { state.pending = null; }, { passive: true });
      element.addEventListener('wheel', () => { state.pending = null; }, { passive: true });
    };
    const align = (element, index, animate) => {
      if (!element?.clientWidth) return;
      const state = controllers.get(element);
      const left = index * element.clientWidth;
      if (Math.abs(element.scrollLeft - left) < 1) { state.pending = null; return; }
      state.pending = index;
      element.scrollTo({ left, behavior: animate && !reduced.matches ? 'smooth' : 'instant' });
    };
    const show = index => {
      if (index < 0 || index >= entries.length) return;
      activate(index);
      align(track, index, !dialog?.open);
      if (dialog?.open) align(dialogTrack, index, true);
    };
    connect(track);
    activate(selected); align(track, selected, false);
    const keyNavigation = event => {
      const next = { ArrowLeft: selected - 1, ArrowRight: selected + 1, Home: 0, End: entries.length - 1 }[event.key];
      if (next === undefined) return;
      event.preventDefault(); show(next);
    };
    track.addEventListener('keydown', keyNavigation);
    thumbs.forEach((btn, index) => {
      btn.addEventListener('click', () => show(index));
      btn.addEventListener('keydown', event => {
        const next = event.key === 'ArrowRight' ? thumbs[index + 1] : event.key === 'ArrowLeft' ? thumbs[index - 1] : null;
        if (next) { event.preventDefault(); next.focus({ preventScroll: true }); show(thumbs.indexOf(next)); }
      });
    });
    const addDialogImage = entry => {
      const slide = document.createElement('div');
      slide.className = 'pdp-lightbox__slide';
      slide.setAttribute('role', 'group');
      const img = document.createElement('img');
      img.dataset.src = entry.full; img.alt = entry.alt || ''; img.loading = 'lazy'; img.decoding = 'async'; img.draggable = false;
      slide.append(img); dialogTrack.append(slide); dialogImages.push(img);
    };
    if (zoom && dialog && typeof dialog.showModal === 'function') {
      dialogTrack = dialog.querySelector('.pdp-lightbox__viewport');
      dialogTrack.replaceChildren();
      dialogTrack.tabIndex = 0;
      dialogTrack.setAttribute('aria-label', 'Photos agrandies du produit');
      entries.forEach(addDialogImage); connect(dialogTrack, true);
      const open = () => {
        dialog.showModal(); align(dialogTrack, selected, false); updateDialog();
      };
      zoom.hidden = false;
      zoom.addEventListener('click', open);
      // Suppress the synthetic click after a drag, without cancelling page scroll or pinch zoom.
      let start, dragged = false, scrolledAt = 0;
      track.addEventListener('scroll', () => { scrolledAt = performance.now(); }, { passive: true });
      track.addEventListener('pointerdown', event => { start = { x: event.clientX, y: event.clientY }; dragged = false; }, { passive: true });
      track.addEventListener('pointermove', event => {
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) dragged = true;
      }, { passive: true });
      track.addEventListener('pointercancel', () => { dragged = true; start = null; }, { passive: true });
      track.addEventListener('click', event => {
        if (!event.target.closest('.pdp__img') || dragged || performance.now() - scrolledAt < 250) return;
        open();
      });
      dialog.querySelector('[data-gallery-prev]').addEventListener('click', () => show(selected - 1));
      dialog.querySelector('[data-gallery-next]').addEventListener('click', () => show(selected + 1));
      dialog.addEventListener('keydown', keyNavigation);
      dialog.addEventListener('close', () => {
        imageRequest++;
        if (document.activeElement === document.body || dialog.contains(document.activeElement)) zoom.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    }
    root.addEventListener('gallery:variant', event => {
      const variant = event.detail;
      let index = entries.findIndex(entry => entry.imageId === String(variant.imageId));
      if (index < 0) {
        const entry = { imageId: String(variant.imageId), full: variant.image, master: variant.imageMaster || variant.image, alt: variant.alt || '', caption: '' };
        index = entries.push(entry) - 1;
        slides.push(makeSlide(entry, index));
        if (dialogTrack) addDialogImage(entry);
      }
      show(index);
    });
    const resize = new ResizeObserver(() => {
      align(track, selected, false);
      if (dialog?.open) align(dialogTrack, selected, false);
    });
    resize.observe(track);
    if (dialogTrack) resize.observe(dialogTrack);
    const unload = event => {
      if (!event.target.contains(root)) return;
      resize.disconnect();
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  }

  /* ─────────────────────── Sélection de variante ─────────────────────── */

  function variants() {
    const form = document.querySelector('[data-product-form]');
    if (!form || form.dataset.variantsReady) return;
    const variantImageMode = document.querySelector('[data-gallery]')?.dataset.variantImageMode || 'variant';
    const data = document.getElementById('variant-data');
    if (!data) return;

    let list;
    try {
      list = JSON.parse(data.textContent);
    } catch (_) {
      return;
    }

    const idField = form.querySelector('[name="id"]');
    const priceWrap = document.querySelector('[data-variant-price-wrap]');
    const priceEl = priceWrap ? priceWrap.querySelector('.num') : null;
    const compareEl = priceWrap ? priceWrap.querySelector('.price__was') : null;
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const submit = form.querySelector('[type="submit"]');
    const submitPrice = submit ? submit.querySelector('[data-submit-price]') : null;

    form.dataset.variantsReady = 'true';
    const apply = () => {
      const chosen = [...form.querySelectorAll('input[type="radio"]:checked')].reduce((acc, input) => {
        acc[Number(input.dataset.optionIndex)] = input.value;
        return acc;
      }, {});
      const matches = list.filter((v) =>
        Object.entries(chosen).every(([i, val]) => v.options[Number(i)] === val)
      );
      const match = matches.find(v => String(v.id) === idField.value && v.available)
        || matches.find(v => v.available) || matches[0];
      const status = document.querySelector('[data-variant-status]');
      const selection = document.querySelector('[data-variant-choice]');
      if (selection) selection.textContent = match ? `Votre sélection : ${match.label || match.options.join(' / ')}` : 'Choisissez une combinaison disponible.';
      const stickyChoice = document.querySelector('[data-sticky-choice]');
      if (stickyChoice) stickyChoice.textContent = match && match.options[0] !== 'Default Title' ? `${match.label || match.options.join(' / ')} · Modifier` : 'Revoir les options';
      document.querySelectorAll('[data-product-contact]').forEach(link => {
        const contactUrl = new URL(link.href, window.location.origin);
        if (match) contactUrl.searchParams.set('variant', match.id);
        else contactUrl.searchParams.delete('variant');
        link.href = contactUrl.pathname + contactUrl.search;
      });
      document.querySelectorAll('[data-size-variant-ids]').forEach(row => {
        const selected = Boolean(match && row.dataset.sizeVariantIds.split('|').includes(String(match.id)));
        if (selected) row.setAttribute('aria-current', 'true');
        else row.removeAttribute('aria-current');
        const marker = row.querySelector('[data-size-current]');
        if (marker) marker.hidden = !selected;
      });
      // Une miniature de couleur doit suivre les autres choix (modèle, finition).
      form.querySelectorAll('input[type="radio"]').forEach(input => {
        const img = input.closest('label')?.querySelector('[data-option-image]');
        if (!img) return;
        const index = Number(input.dataset.optionIndex);
        const candidate = list.find(v => v.options[index] === input.value &&
          Object.entries(chosen).every(([i, val]) => Number(i) === index || v.options[Number(i)] === val));
        img.hidden = !candidate?.image;
        if (candidate?.image) { img.src = sized(candidate.image, 100); img.srcset = ''; }
      });
      const stickyBtn = document.querySelector('[data-sticky-add]');
      const valid = Boolean(match && match.available);
      idField.disabled = !match;
      if (submit) {
        submit.disabled = !valid;
        const label = submit.querySelector('[data-submit-label]');
        if (label) label.textContent = valid ? 'Ajouter au panier' : match ? 'Épuisé' : 'Combinaison indisponible';
      }
      if (stickyBtn) {
        stickyBtn.disabled = !valid;
        stickyBtn.textContent = valid ? 'Ajouter' : 'Indisponible';
      }
      if (status) status.textContent = valid ? 'Disponible à la commande' : match ? 'Cette option est épuisée' : 'Cette combinaison n’existe pas. Choisissez une autre option.';
      if (!match) {
        if (priceEl) priceEl.textContent = '—';
        if (compareEl) compareEl.hidden = true;
        if (submitPrice) submitPrice.textContent = '';
        if (stickyPrice) stickyPrice.textContent = '—';
        const url = new URL(window.location); url.searchParams.delete('variant'); history.replaceState({}, '', url);
        return;
      }
      idField.value = match.id;
      if (priceEl) priceEl.textContent = euro(match.price);
      if (compareEl) {
        const has = match.compare && match.compare > match.price;
        compareEl.hidden = !has;
        if (has) compareEl.textContent = euro(match.compare);
      }
      if (submitPrice) submitPrice.textContent = euro(match.price);
      if (stickyPrice) stickyPrice.textContent = euro(match.price);
      if (match.image && variantImageMode !== 'product-hero') {
        const thumb = document.querySelector(`[data-image-id="${match.imageId}"]`);
        if (thumb) thumb.click();
        else {
          document.querySelector('[data-gallery]')?.dispatchEvent(new CustomEvent('gallery:variant', { detail: match }));
        }
      }
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      history.replaceState({}, '', url);
      const stickyImage = document.querySelector('.stickybuy__ph img');
      if (stickyImage && match.image) stickyImage.src = sized(match.image, 120);
    };

    form.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) apply();
    });
    apply();
  }

  document.addEventListener('shopify:section:load', () => { gallery(); variants(); });

  /* ───────────────────── Description dépliable ───────────────────── */

  function expanders() {
    document.querySelectorAll('[data-expand]').forEach((btn) => {
      const target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      // Rien à déplier si le texte tient déjà.
      if (target.scrollHeight <= target.clientHeight + 4) {
        btn.hidden = true;
        target.classList.remove('is-clamped');
        return;
      }
      btn.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        target.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? 'Réduire' : btn.dataset.expand;
      });
    });
  }




  /* ───────────── Barre d'achat collante (fiche produit) ───────────── */

  function stickyBuy() {
    const bar = document.querySelector('[data-stickybuy]');
    const form = document.querySelector('[data-product-form]');
    if (!bar || !form) return;
    const anchor = form.querySelector('[type="submit"]');
    if (!anchor) return;

    /* Elle apparaît quand le bouton principal a quitté le champ par le haut.
       Un IntersectionObserver ne prévient qu'au franchissement d'un seuil : un
       saut qui enjambe le bouton d'un seul coup, lien d'ancre, retour arrière ou
       geste rapide sur mobile, ne déclenche aucun appel, et la barre restait
       cachée pour le reste de la visite. On lit donc la position réelle, une fois
       par image au plus, à l'écoute passive du défilement. L'état est aussi
       calculé au chargement, pour une arrivée directe au milieu de la page. */
    let pending = false;
    let visible = null;

    /* La barre est fixée au bas du champ : sans réserve, elle recouvre la fin de
       la page, dont la mention de prix et de TVA du pied. On réserve sa hauteur
       réelle, et seulement quand elle est là. */
    const reserve = () => {
      document.body.style.paddingBottom = visible ? bar.offsetHeight + 'px' : '';
    };
    const settle = () => {
      pending = false;
      const passed = anchor.getBoundingClientRect().bottom <= 0;
      if (passed === visible) return;
      visible = passed;
      bar.hidden = !passed;
      reserve();
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(settle);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => {
      schedule();
      reserve();
    }, { passive: true });
    settle();

    bar.querySelector('[data-sticky-add]').addEventListener('click', (e) => {
      submitProduct(form, e.currentTarget);
    });
    document.querySelectorAll('a[href="#product-options"]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      const options = document.getElementById('product-options');
      options?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      (form.querySelector('input[type="radio"]:checked') || form.querySelector('[name="quantity"]') || options)?.focus({ preventScroll: true });
    }));
  }

  /* ──────────────── Recommandations, chargées après coup ──────────────── */

  function recommendations() {
    const host = document.querySelector('[data-recos]');
    if (!host || !host.dataset.url) return;
    const load = () => {
      fetch(host.dataset.url)
        .then((r) => { if (!r.ok) throw new Error('Recommendations unavailable'); return r.text(); })
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recos]');
          if (fresh?.querySelector('.cartel') && !host.contains(document.activeElement)) host.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '600px' }
      );
      io.observe(host);
    } else {
      load();
    }
  }



  /* ─────────────── Pas-à-pas de quantité ───────────────
     Le champ nombre natif reste la source de vérité : les boutons ne font que
     l'incrémenter et déclencher un `change`, pour que tout ce qui écoute le
     formulaire continue de fonctionner sans JavaScript dédié. */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const field = btn.closest('[data-qty-stepper]');
    const input = field && field.querySelector('input');
    if (!input) return;
    e.preventDefault();
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qty));
    if (next === Number(input.value)) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    field.querySelectorAll('[data-qty="-1"]').forEach((b) => { b.disabled = next <= min; });
  });

  function syncSteppers() {
    document.querySelectorAll('[data-qty-stepper]').forEach((f) => {
      const input = f.querySelector('input');
      const minus = f.querySelector('[data-qty="-1"]');
      if (input && minus) minus.disabled = Number(input.value) <= Number(input.min || 0);
    });
  }

  /* ─────────────────── Produits récemment vus ───────────────────
     Écrit et lu dans le navigateur du visiteur. Rien ne part au serveur,
     rien n'est conservé ailleurs que sur son appareil. Toute erreur de
     stockage (navigation privée, quota) est avalée : la section disparaît
     simplement, elle ne casse jamais la page. */

  const RV_KEY = 'minouko:vus';
  const RV_MAX = 12;

  function rvRead() {
    try {
      const raw = localStorage.getItem(RV_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  }

  function rvRemember() {
    const meta = document.getElementById('rv-current');
    if (!meta) return;
    let item;
    try {
      item = JSON.parse(meta.textContent);
    } catch (_) {
      return;
    }
    if (!item || !item.url) return;
    try {
      const list = rvRead().filter((p) => p.url !== item.url);
      list.unshift(item);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (_) {
      /* Stockage indisponible : on n'insiste pas. */
    }
  }

  function rvRender() {
    const host = document.querySelector('[data-recently-viewed]');
    if (!host) return;
    const grid = host.querySelector('[data-rv-grid]');
    const current = window.location.pathname;
    const items = rvRead()
      .filter((p) => p.url !== current)
      .slice(0, Number(host.dataset.count || 4));

    if (!items.length) {
      host.hidden = true;
      return;
    }
    grid.innerHTML = items
      .map(
        (p) => `<a class="rv__item" href="${p.url}">
          <span class="rv__ph">${p.img ? `<img src="${sized(p.img, 420)}" alt="" decoding="async">` : ''}</span>
          <span class="rv__t">${p.title}</span>
          <span class="price num">${euro(p.cents)}</span>
        </a>`
      )
      .join('');
    host.hidden = false;

    const clear = host.querySelector('[data-rv-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        try {
          localStorage.removeItem(RV_KEY);
        } catch (_) {}
        host.hidden = true;
      });
    }
  }

  /* ─────────────────── Tiroir de navigation (mobile) ─────────────────── */

  function navDrawer() {
    let lastFocus = null;
    let savedOverflow = '';
    const inerted = new Map();
    const desktop = window.matchMedia('(min-width: 1260px)');
    const drawer = () => document.getElementById('nav-drawer');
    const summaryOf = group => group.querySelector(':scope > summary');
    const mobileGroup = () => drawer()?.querySelector('[data-menu-family][open]');
    const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motions = new Map();
    let hoverTimer;
    let leaveTimer;
    const clearHover = () => { clearTimeout(hoverTimer); clearTimeout(leaveTimer); };
    const activeDesktop = () => document.querySelector('.header__menu [data-menu-family][open]:not([data-closing])');
    const stopMotion = group => {
      const animations = motions.get(group) || [];
      motions.delete(group);
      animations.forEach(animation => animation.cancel());
    };
    const animateGroup = (group, frames, duration, done, contentFrames = null) => {
      const surface = group.querySelector('.mega-surface');
      if (!surface?.animate) { done?.(); return; }
      const options = { duration: reducedMotion.matches ? 80 : duration, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' };
      const animations = [surface.animate(reducedMotion.matches ? [{ opacity: frames[0].opacity ?? 1 }, { opacity: frames[1].opacity ?? 1 }] : frames, options)];
      if (contentFrames && !reducedMotion.matches) {
        animations.push(group.querySelector('.mega-layout').animate(contentFrames, options));
      }
      motions.set(group, animations);
      animations[0].finished.then(() => {
        if (motions.get(group) !== animations) return;
        done?.();
        stopMotion(group);
      }).catch(() => {}); // A new pointer or keyboard action interrupts the previous transition.
    };
    const dismissGroup = (group, restoreFocus = false, direction = 0, immediate = false) => {
      if (group.hasAttribute('data-closing') && !immediate) return;
      const surface = group.querySelector('.mega-surface');
      const current = getComputedStyle(surface);
      const from = { clipPath: current.clipPath, opacity: current.opacity };
      stopMotion(group);
      if (group.contains(document.activeElement) && (restoreFocus || surface.contains(document.activeElement))) summaryOf(group)?.focus();
      group.setAttribute('data-closing', '');
      summaryOf(group)?.setAttribute('aria-expanded', 'false');
      surface.inert = true;
      const finish = () => {
        group.open = false;
        group.removeAttribute('data-closing');
        surface.inert = false;
      };
      if (immediate) { finish(); return; }
      animateGroup(group, direction
        ? [from, { opacity: 0 }]
        : [from, { clipPath: 'inset(0 0 100% 0)', opacity: 0 }],
      direction ? 240 : 200, finish, direction
        ? [{ transform: 'translateX(0)', opacity: 1 }, { transform: `translateX(${-direction * 18}px)`, opacity: 0 }]
        : null);
    };
    const closeDesktop = (restoreFocus = false, except = null, immediate = false) => {
      clearHover();
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(group => {
        if (group !== except) dismissGroup(group, restoreFocus, 0, immediate);
      });
    };
    const warmImages = group => {
      group.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
      // Warm the five editorial scenes once navigation intent is established.
      document.querySelectorAll('.header__menu .mega-scene').forEach(img => {
        if (group.contains(img)) return;
        img.fetchPriority = 'low';
        img.loading = 'eager';
      });
    };
    const openDesktop = group => {
      clearHover();
      if (!desktop.matches || !group.isConnected || activeDesktop() === group) return;
      warmImages(group);
      const previous = activeDesktop();
      const groups = [...group.parentElement.querySelectorAll('[data-menu-family]')];
      const direction = previous ? Math.sign(groups.indexOf(group) - groups.indexOf(previous)) : 0;
      const previousHeight = previous?.querySelector('.mega-surface').getBoundingClientRect().height;
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(other => {
        if (other !== group) dismissGroup(other, false, direction, other !== previous);
      });
      stopMotion(group);
      group.removeAttribute('data-closing');
      group.querySelector('.mega-surface').inert = false;
      group.open = true;
      summaryOf(group)?.setAttribute('aria-expanded', 'true');
      measure();
      const height = group.querySelector('.mega-surface').getBoundingClientRect().height;
      const crop = previousHeight ? Math.max(0, (1 - previousHeight / height) * 100) : 100;
      animateGroup(group, [
        { clipPath: `inset(0 0 ${crop}% 0)`, opacity: previous ? 1 : 0.7 },
        { clipPath: 'inset(0 0 0% 0)', opacity: 1 }
      ], previous ? 280 : 360, null, previous
        ? [{ transform: `translateX(${direction * 22}px)`, opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }]
        : null);
    };
    // Delegation keeps hover working after Shopify replaces the header section.
    document.addEventListener('pointerover', event => {
      if (!desktop.matches || !hover.matches || event.pointerType === 'touch') return;
      const group = event.target.closest('.header__menu [data-menu-family]');
      if (!group) return;
      clearTimeout(leaveTimer);
      if (group.contains(event.relatedTarget)) return;
      clearTimeout(hoverTimer);
      warmImages(group);
      hoverTimer = setTimeout(() => openDesktop(group), activeDesktop() ? 90 : 120);
    });
    document.addEventListener('pointerout', event => {
      if (!desktop.matches || !hover.matches || event.pointerType === 'touch') return;
      const group = event.target.closest('.header__menu [data-menu-family]');
      if (!group || group.contains(event.relatedTarget)) return;
      clearTimeout(hoverTimer);
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => {
        // Keyboard users may continue reading the panel after the pointer leaves.
        if (!activeDesktop()?.contains(document.activeElement)) closeDesktop();
      }, 260);
    });
    const measure = () => {
      const header = document.querySelector('[data-mega-header]');
      if (!header) return;
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      header.parentElement.style.setProperty('--mega-header-bottom', `${bottom}px`);
    };
    let mobileMotion = null;
    let mobileTarget = null;
    let mobileReturnFocus = null;
    let familyScrollTop = 0;
    let mobileSequence = 0;
    const stopMobileMotion = () => {
      mobileSequence++;
      mobileMotion?.cancel();
      mobileMotion = null;
      const nav = drawer()?.querySelector('.mega-mobile');
      if (nav) {
        nav.inert = false;
        nav.removeAttribute('data-mobile-transition');
      }
      mobileTarget = null;
    };
    const resetFamilies = () => {
      stopMobileMotion();
      drawer()?.querySelectorAll('[data-menu-family][open]').forEach(group => { group.open = false; });
      const nav = drawer()?.querySelector('.mega-mobile');
      if (nav) nav.scrollTo({ top: 0, behavior: 'instant' });
      familyScrollTop = 0;
      mobileReturnFocus = null;
    };
    const transitionMobile = target => {
      const el = drawer();
      const nav = el?.querySelector('.mega-mobile');
      if (!nav || !el.hasAttribute('open') || desktop.matches) return;
      const currentGroup = mobileGroup();
      if (target && !currentGroup && !mobileTarget) familyScrollTop = nav.scrollTop;
      if (target) mobileReturnFocus = summaryOf(target);
      const current = getComputedStyle(nav);
      const from = { opacity: current.opacity, transform: current.transform };
      stopMobileMotion();
      const sequence = mobileSequence;
      mobileTarget = target;
      const direction = target ? 1 : -1;
      nav.dataset.mobileTransition = target ? 'forward' : 'back';
      const valid = () => sequence === mobileSequence && nav.isConnected && el.hasAttribute('open') && !desktop.matches;
      const swap = () => {
        nav.querySelectorAll('[data-menu-family][open]').forEach(group => { group.open = false; });
        if (target) target.open = true;
        nav.scrollTo({ top: target ? 0 : familyScrollTop, behavior: 'instant' });
        nav.inert = false;
        const focus = target ? target.querySelector('.mega-intro-title') : mobileReturnFocus;
        focus?.focus({ preventScroll: true });
      };
      if (!nav.animate) { swap(); stopMobileMotion(); return; }
      const frames = (start, end) => reducedMotion.matches
        ? [{ opacity: start.opacity }, { opacity: end.opacity }]
        : [start, end];
      const animate = (keyframes, duration) => nav.animate(keyframes, {
        duration: reducedMotion.matches ? 60 : duration,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both'
      });
      // Keep the outgoing links out of the focus order during the brief exit.
      // The drawer's close button and Escape remain available throughout.
      nav.inert = true;
      mobileMotion = animate(frames(from, { opacity: 0, transform: `translateX(${-direction * 24}px)` }), target ? 110 : 90);
      mobileMotion.finished.then(() => {
        if (!valid()) return;
        swap();
        mobileMotion.cancel();
        mobileMotion = animate(frames(
          { opacity: 0, transform: `translateX(${direction * 32}px)` },
          { opacity: 1, transform: 'translateX(0)' }
        ), target ? 240 : 210);
        return mobileMotion.finished.then(() => {
          if (valid()) stopMobileMotion();
        });
      }).catch(() => {}); // A return, close or resize supersedes the previous destination.
    };
    const returnToFamilies = () => {
      if (!mobileGroup() && !mobileTarget) return false;
      transitionMobile(null);
      return true;
    };
    const isolateDrawer = el => {
      // Isolate siblings at every ancestor level, including the header itself.
      // Restore previous inert states instead of clearing another component's state.
      for (let parent = el; parent && parent !== document.body; parent = parent.parentElement) {
        for (const sibling of parent.parentElement?.children || []) {
          if (sibling === parent || inerted.has(sibling)) continue;
          inerted.set(sibling, sibling.inert);
          sibling.inert = true;
        }
      }
    };
    const close = (restoreFocus = true) => {
      const el = drawer();
      if (!el?.hasAttribute('open') && !inerted.size) return;
      stopMobileMotion();
      el?.removeAttribute('open');
      if (el) el.inert = true;
      document.documentElement.style.overflow = savedOverflow;
      inerted.forEach((previous, node) => { node.inert = previous; });
      inerted.clear();
      document.querySelectorAll('[data-nav-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      // Keep the selected family visible during the closing transition.
      // Opening the drawer resets it before it becomes interactive again.
      if (restoreFocus && lastFocus?.isConnected) lastFocus.focus();
    };
    const open = trigger => {
      const el = drawer();
      if (!el || desktop.matches || el.hasAttribute('open')) return;
      closeDesktop();
      resetFamilies();
      lastFocus = trigger;
      savedOverflow = document.documentElement.style.overflow;
      isolateDrawer(el);
      el.inert = false;
      el.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      trigger.setAttribute('aria-expanded', 'true');
      el.querySelector('button[data-nav-close]')?.focus();
    };
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-nav-open]');
      if (trigger) { event.preventDefault(); open(trigger); return; }
      if (event.target.closest('[data-nav-close]')) { close(); return; }
      const summary = event.target.closest('[data-menu-family] > summary');
      const group = summary?.parentElement;
      if (group?.closest('#nav-drawer')) {
        event.preventDefault();
        if (group.open || mobileTarget === group) { returnToFamilies(); return; }
        transitionMobile(group);
        return;
      }
      if (group?.closest('.header__menu')) {
        event.preventDefault();
        if (activeDesktop() === group) closeDesktop();
        else openDesktop(group);
        return;
      }
      clearHover();
      if (event.target.closest('[data-search-open]')) close(false);
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(openGroup => {
        if (!openGroup.contains(event.target)) dismissGroup(openGroup);
      });
    });
    document.addEventListener('keydown', event => {
      const el = drawer();
      if (event.key === 'Escape') {
        if (el?.hasAttribute('open')) {
          event.preventDefault();
          if (!returnToFamilies()) close();
        } else closeDesktop(true);
      }
      if (event.key === '/' && el?.hasAttribute('open') && !/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) close(false);
      if (event.key === 'Tab' && el?.hasAttribute('open')) {
        const nodes = [...el.querySelectorAll(focusable)].filter(node => node.getClientRects().length && !node.closest('[inert]'));
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    });
    document.addEventListener('focusin', event => {
      clearHover();
      const el = drawer();
      if (el?.hasAttribute('open') && !el.contains(event.target)) {
        el.querySelector('button[data-nav-close]')?.focus();
        return;
      }
      document.querySelectorAll('.header__menu [data-menu-family][open]').forEach(group => {
        if (!group.contains(event.target)) dismissGroup(group);
      });
    });
    document.addEventListener('toggle', event => {
      if (event.target.matches('.header__menu [data-menu-family][open]')) measure();
    }, true);
    desktop.addEventListener('change', () => {
      close(false);
      closeDesktop(false, null, true);
      measure();
      if (document.activeElement?.closest('#nav-drawer, .header__menu')) {
        const target = desktop.matches
          ? document.querySelector('.header__menu summary, .header__menu > a')
          : document.querySelector('[data-nav-open]');
        target?.focus();
      }
    });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('scroll', () => {
      if (document.querySelector('.header__menu [data-menu-family][open]')) measure();
    }, { passive: true });
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.querySelector('#nav-drawer')) {
        close(false);
        closeDesktop(false, null, true);
      }
    });
    document.addEventListener('shopify:section:load', () => {
      // A replacement header can arrive without an unload event.
      if (inerted.size && !drawer()?.hasAttribute('open')) close(false);
      measure();
    });
    document.addEventListener('shopify:block:select', event => {
      const group = event.target.closest('[data-menu-family]');
      if (!group) return;
      if (desktop.matches) {
        openDesktop(group);
      } else {
        open(document.querySelector('[data-nav-open]'));
        resetFamilies();
        const matching = [...drawer().querySelectorAll('[data-menu-family]')].find(item => item.dataset.menuTitle === group.dataset.menuTitle);
        if (matching) matching.open = true;
      }
    });
    measure();
  }

  /* ───────────────── Filtrage et tri de collection ───────────────── */

  function catalog() {
    if (document.documentElement.dataset.catalogReady) return;
    const firstRoot = document.querySelector('[data-catalog]');
    if (!firstRoot) return;
    document.documentElement.dataset.catalogReady = 'true';

    const desktop = window.matchMedia('(min-width: 900px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let controller = null;

    const setupRoot = (root) => {
      if (!root) return;
      root.dataset.catalogEnhanced = 'true';
      const filters = root.querySelector('.catalog-filters');
      if (filters) filters.open = desktop.matches;
    };

    const setupCurrentRoot = () => setupRoot(document.querySelector('[data-catalog]'));

    const requestedUrl = (form) => {
      const url = new URL(form.action, window.location.href);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, name) => {
        if (typeof value === 'string' && value.trim() !== '') params.append(name, value);
      });
      url.search = params.toString();
      url.hash = 'collection-products';
      return url;
    };

    const focusSnapshot = (element) => {
      if (!element || !element.closest('[data-catalog]')) return null;
      return {
        id: element.id || '',
        name: element.name || '',
        value: element.value || '',
        type: element.type || '',
      };
    };

    const restoreFocus = (root, snapshot, fallback) => {
      let target = null;
      if (snapshot?.id) target = root.querySelector(`#${CSS.escape(snapshot.id)}`);
      if (!target && snapshot?.name) {
        target = [...root.querySelectorAll(`[name="${CSS.escape(snapshot.name)}"]`)].find(
          (field) => !snapshot.value || field.value === snapshot.value
        );
      }
      if (target?.matches('select') && target.closest('[data-enhanced]')) {
        target = target.closest('[data-ui-select]')?.querySelector('.ui-select__trigger') || target;
      }
      if (!target && fallback) target = root.querySelector(fallback);
      target?.focus({ preventScroll: true });
    };

    const announce = (root) => {
      const status = root.querySelector('[data-catalog-status]');
      const count = root.querySelector('[data-catalog-count]')?.textContent.trim();
      const range = root.querySelector('.collection-pagination__count')?.textContent.trim();
      if (status && count) status.textContent = `Sélection mise à jour : ${range || count}.`;
    };

    const update = async (url, { push = true, focus = null, fallback = '', scrollToProducts = false, animate = true } = {}) => {
      const root = document.querySelector('[data-catalog]');
      if (!root) return;
      const filtersWereOpen = root.querySelector('.catalog-filters')?.open || false;
      controller?.abort();
      controller = new AbortController();
      const currentController = controller;
      const deadline = setTimeout(() => currentController.abort(), 8000);
      const status = root.querySelector('[data-catalog-status]');
      root.toggleAttribute('data-catalog-static', !animate);
      root.dataset.catalogLoading = 'true';
      root.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Mise à jour de la sélection…';

      const fetchUrl = new URL(url, window.location.href);
      fetchUrl.hash = '';
      fetchUrl.searchParams.set('section_id', root.dataset.sectionId);

      try {
        const response = await fetch(fetchUrl, {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: currentController.signal,
        });
        if (!response.ok) throw new Error(`Réponse ${response.status}`);
        const html = await response.text();
        if (controller !== currentController) return;
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = nextDocument.querySelector('[data-catalog]');
        if (!nextRoot) throw new Error('Section de collection absente');

        const swap = () => {
          if (controller !== currentController || !root.isConnected) return;
          root.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true }));
          root.replaceWith(nextRoot);
          setupRoot(nextRoot);
          if (!desktop.matches && filtersWereOpen) {
            const nextFilters = nextRoot.querySelector('.catalog-filters');
            if (nextFilters) nextFilters.open = true;
          }
          if (animate) nextRoot.dataset.catalogEntering = 'true';
          nextRoot.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
          requestAnimationFrame(() => requestAnimationFrame(() => delete nextRoot.dataset.catalogEntering));
        };

        if (animate && document.startViewTransition && !reduceMotion.matches) {
          try {
            const transition = document.startViewTransition(swap);
            transition.ready.catch(() => {});
            transition.updateCallbackDone.catch(() => {});
            await transition.finished.catch(() => {});
          } catch (_) {
            if (!nextRoot.isConnected) swap();
          }
        } else {
          swap();
        }

        if (controller !== currentController) return;
        if (push) window.history.pushState({}, '', url);
        restoreFocus(nextRoot, focus, fallback);
        if (scrollToProducts) nextRoot.scrollIntoView({ block: 'start', behavior: 'auto' });
        announce(nextRoot);
      } catch (error) {
        if (controller !== currentController) return;
        root.removeAttribute('data-catalog-loading');
        root.removeAttribute('aria-busy');
        if (status) {
          status.textContent = 'La mise à jour n’a pas abouti. ';
          const fallback = document.createElement('a');
          fallback.href = new URL(url, window.location.href).href;
          fallback.textContent = 'Ouvrir les résultats';
          status.append(fallback);
        }
      } finally {
        clearTimeout(deadline);
        if (controller === currentController) controller = null;
      }
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-catalog-form]');
      if (!form) return;
      event.preventDefault();
      update(requestedUrl(form), { focus: focusSnapshot(document.activeElement) });
    });

    document.addEventListener('change', (event) => {
      const field = event.target.closest('[data-catalog-form] :is(input, select)');
      if (!field || !field.form || !field.form.reportValidity()) return;
      update(requestedUrl(field.form), { focus: focusSnapshot(field) });
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest(
        '[data-catalog] :is(.catalog-active a, [data-catalog-reset], .catalog-empty a[href*="collection-products"], .collection-pagination__nav a)'
      );
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const isPagination = Boolean(link.closest('.collection-pagination__nav'));
      update(new URL(link.href, window.location.href), {
        fallback: '#collection-products-title',
        scrollToProducts: isPagination,
        animate: !isPagination,
      });
    });

    window.addEventListener('popstate', () => {
      if (document.querySelector('[data-catalog]')) update(window.location.href, { push: false, animate: false });
    });
    desktop.addEventListener('change', setupCurrentRoot);
    setupRoot(firstRoot);
  }

  /* ─────────────────────────── Démarrage ─────────────────────────── */

  const boot = () => {
    const contactRef = document.getElementById('ContactForm-reference');
    if (contactRef && !contactRef.value) {
      const params = new URLSearchParams(window.location.search);
      const handle = params.get('product') || '';
      const variant = params.get('variant') || '';
      if (/^[a-z0-9-]{1,100}$/.test(handle)) {
        contactRef.value = handle.replaceAll('-', ' ').slice(0, 120);
        const body = document.getElementById('ContactForm-body');
        if (body && !body.value) body.value = `Bonjour,\n\nJ’ai une question concernant ce produit :\n${window.location.origin}/products/${handle}${/^\d+$/.test(variant) ? '?variant=' + variant : ''}\n\n`;
        const topic = document.getElementById('ContactForm-topic');
        if (topic && !topic.value) {
          topic.value = params.get('subject') === 'retours' ? 'Retour ou remboursement' : params.get('subject') === 'livraison' ? 'Commande ou livraison' : 'Conseil avant achat';
          topic.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    Drawer.init();
    navDrawer();
    stickyBuy();
    recommendations();
    rvRemember();
    rvRender();
    syncSteppers();
    gallery();
    variants();
    expanders();
    catalog();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
